import React, { useMemo } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import { isEmpty, meanBy } from 'lodash'
import { extent } from 'd3-array'
import { scaleLinear } from 'd3-scale'

// components
import { SectionTitle, SectionTitleContainer } from 'components/Panel/common'
import Svg from './Svg'
import Row from './Row'

// hooks
import { usePopulationSetting } from 'flow/settings/population'

// utils
import FormatNumber from 'common/utils/number'
import getNature from 'common/utils/get-nature'
import { FormattedMessage } from 'react-intl'

const Container = styled.div(({ theme, isConcept }) => css`
  background-color: ${isConcept ? theme.purple : theme.blueUltraDark};
  position: sticky;
  z-index: 2;
  top: -60px; /* PanelContainer padding-top */
  padding-top: 13px;
  padding-bottom: 7px;
  margin-bottom: 35px;
  border-bottom: 3px solid ${theme.separator};
`)
const AbacusContainer = styled.div`
  position: relative;
`
const Nature = styled.span`
  padding-right: 5px;
`
const Period = styled.span`
  text-transform: lowercase;
`

const SectionTitleContainerStyled = styled(SectionTitleContainer)`
  margin-bottom: 28px;
`

// constants
const rowHeight = 61
const rowWidth = 370

const indicators = [
  {
    label: 'Engagement',
    accessor: n => n && n.average_attempts_per_question,
    unit: x => x > 1 ? 'attempts' : 'attempt',
    tooltip: 'tooltip-average_attempts_per_question',
    keepWhenPopulationSelected: true,
    showAverage: false
  },
  {
    label: 'Mastery',
    accessor: n => n && n.average_mastery,
    unit: x => '',
    tooltip: 'tooltip-average_mastery',
    keepWhenPopulationSelected: true,
    showAverage: true
  },
  {
    label: 'Content',
    accessor: n => n && n.n_unique_questions,
    unit: x => x > 1 ? 'questions' : 'question',
    tooltip: 'tooltip-n_unique_questions',
    keepWhenPopulationSelected: false,
    showAverage: true
  },
  {
    label: 'Number of students',
    accessor: n => n && n.n_unique_users,
    unit: x => '',
    tooltip: 'tooltip-n_unique_users',
    keepWhenPopulationSelected: false,
    showAverage: false
  }
]

const Abacus = ({ nodes, isConcept, currentNode, selection, setSelection }) => {
  const [selectedPopulation] = usePopulationSetting()
  const indicatorsFiltered = useMemo(() => {
    return indicators.filter(indicator => {
      return selectedPopulation.value === 'all' || (selectedPopulation.value !== 'all' && indicator.keepWhenPopulationSelected)
    })
  }, [selectedPopulation])

  const selectionCleaned = selection.filter((d) => getNature(d.id) !== 'competency')
  const highlightedIds = selectionCleaned.filter((d) => !d.isFixed).map((d) => d.id)

  const isPrimaryParent = useMemo(() => {
    return selectionCleaned.length > 0 && !nodes.some((d) => selectionCleaned[0].id === d.data.id)
  }, [nodes, selectionCleaned])

  const isSecondaryParent = useMemo(() => {
    return selectionCleaned.length === 2 && !nodes.some((d) => selectionCleaned[1].id === d.data.id)
  }, [nodes, selectionCleaned])

  const scales = useMemo(() => {
    return indicatorsFiltered.map((indicator) => {
      const nodesData = nodes.map((d) => indicator.accessor(d.userData))
      const nodesDataAll = indicator.showAverage ? nodes.map((d) => indicator.accessor(d.userDataAll)) : []

      const values = [
        ...nodesData,
        ...nodesDataAll
      ]
      const bounds = extent(values)

      return {
        fn: scaleLinear()
          .domain(bounds)
          .range([1, rowWidth - 1])
          .clamp(true),
        bounds,
        min: {
          text: FormatNumber(bounds[0]),
          unit: indicator.unit(bounds[0])
        },
        max: {
          text: FormatNumber(bounds[1]),
          unit: indicator.unit(bounds[1])
        }
      }
    })
  }, [nodes, indicatorsFiltered])

  const rowNodesWithLayout = useMemo(() => {
    return indicatorsFiltered.map((indicator, i) => {
      return nodes.map((d) => {
        const value = indicator.accessor(d.userData)
        const valueAll = indicator.accessor(d.userDataAll)
        const valueInPx = value ? scales[i].fn(value) : 0
        const rounded = Math.round(valueInPx)

        return {
          ...d,
          x: rounded,
          value,
          valueAll,
          textValue: FormatNumber(value)
        }
      })
    })
  }, [nodes, scales, indicatorsFiltered])

  const rowNodesWithLayoutState = useMemo(() => {
    return rowNodesWithLayout.map((row) => {
      return row.map((node) => {
        const primary = selectionCleaned.length >= 1 && selectionCleaned[0].id
        const secondary = selectionCleaned.length === 2 && selectionCleaned[1].id

        return {
          ...node,
          isPrimary: primary && (primary === node.data.id || (node.parent && primary === node.parent.data.id)),
          isSecondary: secondary && (secondary === node.data.id || (node.parent && secondary === node.parent.data.id))
        }
      })
    })
  }, [rowNodesWithLayout, selectionCleaned])

  if (isEmpty(nodes)) return null

  const nature = nodes[0].data.nature ? `${nodes[0].data.nature}s` : currentNode.data.nature
  const populationId = selectedPopulation.value !== 'all' && selectedPopulation.value

  return (
    <Container
      isConcept={isConcept}
    >
      <SectionTitleContainerStyled>
        <SectionTitle opacify>
          <Nature>
            <FormattedMessage
              id='abacus-title'
              values={{ NATURE: nature }}
            />
          </Nature>
          {
            populationId && (
              <>
                <FormattedMessage
                  id='abacus-population'
                  values={{ POPULATION_ID: populationId }}
                /><br />
              </>
            )
          }
          <Period>
            <FormattedMessage id='abacus-period' />
          </Period>
        </SectionTitle>
      </SectionTitleContainerStyled>
      <AbacusContainer>
        {
          rowNodesWithLayoutState.map((nodes, i) => {
            const indicator = indicatorsFiltered[i]
            const average = populationId && indicator.showAverage && meanBy(nodes, 'valueAll')
            const averageInPx = average ? scales[i].fn(average) : null
            const averagePosition = averageInPx && Math.round(averageInPx)

            return (
              <Row
                key={i}
                isConcept={isConcept}
                currentNode={currentNode}
                indicator={indicator}
                index={i}
                nodes={nodes}
                width={rowWidth}
                height={rowHeight}
                scale={scales[i]}
                populationId={populationId}
                averagePosition={averagePosition}
                selection={selection}
                setSelection={setSelection}
                isPrimaryParent={isPrimaryParent}
                isSecondaryParent={isSecondaryParent}
              />
            )
          })
        }
        <Svg
          width={rowWidth}
          height={rowHeight * indicatorsFiltered.length}
          rowNodesWithLayoutState={rowNodesWithLayoutState}
          isPrimaryParent={isPrimaryParent}
          isSecondaryParent={isSecondaryParent}
          highlightedIds={highlightedIds}
          selection={selectionCleaned}
        />
      </AbacusContainer>
    </Container>
  )
}

export default Abacus
